import React, { useEffect, useRef } from 'react';
import NewPopupLayout from '../common/NewPopupLayout';
import { PoseItem, BodyAreaItem } from '../constants/bodyAreaData';
export type { PoseItem, BodyAreaItem };

interface BodyAreaPopupProps {
  onCloseIconClicked: () => void;
  isOpen: boolean;
  bodyAreas: BodyAreaItem[];
}

const PopupLayout = NewPopupLayout as unknown as React.FC<{
  onCloseIconClicked: () => void;
  customClass?: string;
  children: React.ReactNode;
}>;

const painColorScale: { level: string; color: string }[] = [
  { level: 'Very Tight', color: '#FF0004' },
  { level: 'Tight', color: '#FF8184' },
  { level: 'Moderate', color: '#F3A742' },
  { level: 'Flexible', color: '#2AD76F' },
  { level: 'Very Flexible', color: '#2AD76F' }
];

const BodyAreaPopup = ({ onCloseIconClicked, isOpen, bodyAreas }: BodyAreaPopupProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      // Scroll the fixed overlay (scrollable ancestor) back to top when popup opens
      let el: HTMLElement | null = contentRef.current.parentElement;
      while (el) {
        const overflow = window.getComputedStyle(el).overflowY;
        if (overflow === 'scroll' || overflow === 'auto') {
          el.scrollTop = 0;
          break;
        }
        el = el.parentElement;
      }
    }
  }, [isOpen]);

  return (
    <PopupLayout onCloseIconClicked={onCloseIconClicked} customClass="md:w-[680px]">
      <div ref={contentRef} className="flex flex-col gap-[15px] pb-[45px] md:pb-0">
        {bodyAreas.map((area, areaIndex) => (
          <div key={areaIndex} className="flex flex-col gap-[5px] md:gap-[20px]">
            {/* Header */}
            <div className="flex flex-col gap-[4px] md:mt-[5px]">
              <h2 className="text-[17px] leading-[22px] md:text-[20px] md:leading-normal font-Inter font-semibold text-[#333] md:text-[#000]">
                {area.name}
              </h2>
              <p className="text-[12.5px] leading-[26px] font-Inter font-[560] text-[#A3A3A3] m-0">
                Current Health:{' '}
                <span
                  className="font-[560]"
                  style={{
                    color:
                      painColorScale.find(
                        (p) => p.level.toLowerCase() === area.currentHealth.toLowerCase()
                      )?.color ?? '#A3A3A3'
                  }}
                >
                  {area.currentHealth}
                </span>
              </p>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-[12px]">
              {area.descriptions.map((desc, descIndex) => (
                <p
                  key={descIndex}
                  className="text-[14.5px] leading-[20px] md:leading-[26px] font-Inter font-normal text-[#757575] m-0"
                >
                  {desc}
                </p>
              ))}
              {/* Pose bullet points */}
              {area.poses.some((p) => p.description) && (
                <ul className="flex flex-col gap-[6px] list-none">
                  {area.poses
                    .filter((p) => p.description)
                    .map((pose, poseIndex) => (
                      <li
                        key={poseIndex}
                        className="text-[14.5px] leading-[20px] md:leading-[26px] font-Inter font-normal text-[#757575] flex gap-[8px]"
                      >
                        <span className="mt-[2px] shrink-0">•</span>
                        <span>
                          <span className="font-medium text-[#757575]">{pose.name}</span> –{' '}
                          {pose.description}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            {/* Yoga Poses */}
            {/* Mobile: stacked cards */}
            <div className="flex flex-col gap-[16px] mt-[20px] h-[350px] overflow-y-auto md:hidden">
              {area.poses.map((pose, poseIndex) => (
                <div key={poseIndex} className="flex flex-col gap-[12px]">
                  <p className="text-[15px] leading-normal font-Inter font-medium text-[#000] m-0">
                    {pose.name}
                  </p>
                  <div className="w-full rounded-[13px] bg-[#e8e8e8] overflow-hidden flex items-center justify-center min-h-[180px]">
                    {pose.image ? (
                      <img
                        src={pose.image}
                        alt={pose.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full min-h-[180px] bg-[#e8e8e8]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: separate title row + image row so images always align */}
            <div className="hidden md:flex flex-col gap-[12px] mt-[10px]">
              <div className="flex gap-[18px]">
                {area.poses.map((pose, poseIndex) => (
                  <p
                    key={poseIndex}
                    className="flex-1 text-[15px] leading-normal font-Inter font-medium text-[#000] m-0"
                  >
                    {pose.name}
                  </p>
                ))}
              </div>
              <div className="flex gap-[18px]">
                {area.poses.map((pose, poseIndex) => (
                  <div
                    key={poseIndex}
                    className="flex-1 rounded-[13px] bg-[#e8e8e8] overflow-hidden flex items-center justify-center aspect-square"
                  >
                    {pose.image ? (
                      <img
                        src={pose.image}
                        alt={pose.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#e8e8e8]" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </PopupLayout>
  );
};

export default BodyAreaPopup;
